<template>
  <!--
    Insurance Catalog V2 — Wave-2 integrated page.
    Keeps the Wave-1 shell (calm-page, loadCatalog on mount, show-inactive toggle,
    orphan bucket, responsive isWide). Replaces the placeholder browse + inspector
    with the real BrowseColumns / BrowseDrill / InsuranceInspector components and
    wires every action (create / edit / deactivate / reactivate / move) to the
    "Insure" store via InsCreateEditForm.
  -->
  <calm-page :title="pageTitle" extra-class="ins-catalog-v2">
    <template #eyebrow>Insurance · v2</template>
    <template #sub>
      <span><strong class="c-mono">{{ roots.length }}</strong> หมวดหลัก</span>
      <span class="calm__header__sep" v-if="orphans.length"> · </span>
      <span v-if="orphans.length" class="ins-cat__orphan-flag">
        ⚠ {{ orphans.length }} กำพร้า
      </span>
    </template>

    <!-- ═══════════════ TOOLBAR ═══════════════ -->
    <div class="ins-cat__toolbar">
      <div class="ins-cat__search">
        <el-input
          v-model="searchQuery"
          clearable
          size="small"
          placeholder="ค้นหาในแคตตาล็อก…"
        />
      </div>
      <label class="ins-cat__inactive-toggle">
        <el-switch v-model="includeInactive" @change="onIncludeInactiveChange" />
        <span>แสดงรายการที่ปิดใช้งาน</span>
      </label>
      <label class="ins-cat__inactive-toggle">
        <el-switch v-model="stripPrefix" @change="onStripPrefixChange" />
        <span>{{ $t("lmg.insure_strip_prefix") }}</span>
      </label>
      <label class="ins-cat__inactive-toggle">
        <el-switch v-model="hideTypeChip" @change="onHideTypeChipChange" />
        <span>{{ $t("lmg.insure_hide_type") }}</span>
      </label>
    </div>

    <!-- ═══════════════ MAIN: BROWSE + INSPECTOR ═══════════════ -->
    <div class="ins-cat__layout" :class="isWide ? 'is-wide' : 'is-narrow'">
      <!-- ───── BROWSE ───── -->
      <section class="ins-cat__browse calm__card">
        <div class="calm__card__head">
          <div>
            <div class="calm__card__title">แคตตาล็อก</div>
            <div class="calm__card__hint">
              {{ isWide ? "โหมดคอลัมน์ (Miller)" : "โหมดไล่ระดับ (Drill)" }}
            </div>
          </div>
        </div>

        <div v-if="loading" class="ins-cat__state">กำลังโหลด…</div>
        <div v-else-if="!roots.length && !orphans.length" class="ins-cat__state">
          ไม่พบรายการ
        </div>

        <!-- SEARCH MODE: a non-empty query replaces the browse area with the
             path-chip results list. The orphan bucket below stays visible. -->
        <search-path-chips
          v-else-if="searchQuery.trim()"
          :results="searchResults"
          :query="searchQuery"
          :strip-prefix="stripPrefix"
          :hide-type-chip="hideTypeChip"
          @pick="onSearchPick"
          @pickCrumb="onSearchPick"
        />

        <template v-else>
          <!-- Wide: Miller columns. Narrow: breadcrumb drill. -->
          <browse-columns
            v-if="isWide"
            :roots="roots"
            :childrenOf="catalogChildrenOf"
            :selectionPath="selectionPathIds"
            :selectedId="selectedId"
            :strip-prefix="stripPrefix"
            :hide-type-chip="hideTypeChip"
            @select="onSelect"
          />
          <browse-drill
            v-else
            :breadcrumb="drillBreadcrumb"
            :currentChildren="drillChildren"
            :selectedId="selectedId"
            :childCountOf="childCountOf"
            :strip-prefix="stripPrefix"
            :hide-type-chip="hideTypeChip"
            @select="onSelect"
            @drill="onDrill"
            @up="onUp"
          />
        </template>

        <!-- ───── ORPHAN / UNLINKED BUCKET (never hidden — financial safety) ───── -->
        <div v-if="orphans.length" class="ins-cat__orphans">
          <div class="ins-cat__orphans__head">⚠ ไม่ผูกสายงาน / Unlinked (กำพร้า)</div>
          <div class="ins-cat__rows">
            <ins-node-row
              v-for="o in orphans"
              :key="'orphan-' + o.ID"
              :node="o"
              :has-children="hasChildren(o.ID)"
              :selected="selectedId === o.ID"
              :expanded="false"
              :strip-prefix="stripPrefix"
              :hide-type-chip="hideTypeChip"
              @select="onSelect"
              @toggle="onSelect"
            />
          </div>
        </div>
      </section>

      <!-- ───── INSPECTOR (right pane wide / bottom narrow) ───── -->
      <aside class="ins-cat__inspector calm__card">
        <insurance-inspector
          :node="selectedNode"
          :ancestors="inspectorAncestors"
          :childCount="selectedChildCount"
          :strip-prefix="stripPrefix"
          @add-child="openCreateChild"
          @add-sibling="openCreateSibling"
          @edit="openEdit"
          @deactivate="onDeactivate"
          @reactivate="onReactivate"
          @move="onMove"
        />
      </aside>
    </div>

    <!-- ═══════════════ CREATE / EDIT FORM ═══════════════ -->
    <ins-create-edit-form
      :show="formShow"
      :mode="formMode"
      :contextNode="formNode"
      :ancestors="formAncestors"
      :errorMsg="formError"
      @submit="onSubmitForm"
      @close="closeForm"
    />
  </calm-page>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { Switch, Input } from "element-ui";
import swal from "sweetalert2";
import InsNodeRow from "@/components/lmg/insurance/InsNodeRow.vue";
import BrowseColumns from "@/components/lmg/insurance/BrowseColumns.vue";
import BrowseDrill from "@/components/lmg/insurance/BrowseDrill.vue";
import InsuranceInspector from "@/components/lmg/insurance/InsuranceInspector.vue";
import InsCreateEditForm from "@/components/lmg/insurance/InsCreateEditForm.vue";
import SearchPathChips from "@/components/lmg/insurance/SearchPathChips.vue";

const WIDE_MQ = "(min-width: 1025px)";

// localStorage key for the DISPLAY-ONLY "hide outline numbers" toggle. Default
// is ON (true) — the operator wants the numbers hidden to fix readability.
const STRIP_PREFIX_KEY = "ins_strip_prefix";

// localStorage key for the DISPLAY-ONLY "hide dataType chip" toggle. Default is
// ON (true) — the per-row chip is redundant (inherited from the subtree root)
// and eats name width in the narrow Miller columns, so hide it by default.
const HIDE_TYPE_CHIP_KEY = "ins_hide_type_chip";

// Backend Msg key → localized inline / swal message.
// The backend returns these WITH the `errors.` prefix (e.g. errors.duplicate_insurance_name).
const ERROR_MSG = {
  "errors.duplicate_insurance_name": "ชื่อนี้มีอยู่แล้วในระดับเดียวกัน",
  "errors.insurance_is_upper": "ปิดใช้งานไม่ได้: รายการนี้มีรายการลูกอยู่",
  "errors.insurance_is_using": "ปิดใช้งานไม่ได้: รายการนี้ถูกใช้ในผังค่าคอมมิชชัน",
};

export default {
  name: "InsuranceCatalogV2",
  components: {
    InsNodeRow,
    BrowseColumns,
    BrowseDrill,
    InsuranceInspector,
    InsCreateEditForm,
    SearchPathChips,
    [Switch.name]: Switch,
    [Input.name]: Input,
  },
  data() {
    return {
      selectedId: null,
      drillParentId: null, // narrow-mode: whose children are shown (null = roots)
      searchQuery: "",
      includeInactive: false,
      // DISPLAY-ONLY toggle: hide the leading "a.b.c " outline-number prefix
      // from shown names. Defaults to true; the real read happens on mount from
      // localStorage with a safe fallback to true.
      stripPrefix: true,
      // DISPLAY-ONLY toggle: hide the per-row dataType chip so the name
      // reclaims the freed width. Defaults to true; the real read happens on
      // mount from localStorage with a safe fallback to true.
      hideTypeChip: true,
      isWide: true,
      // form state
      formShow: false,
      formMode: "create-child",
      formNode: null,
      formAncestors: [],
      formError: "",
    };
  },
  computed: {
    ...mapGetters("Insure", {
      catalogById: "catalogById",
      catalogChildrenOf: "catalogChildrenOf",
      catalogRoots: "catalogRoots",
      catalogAncestors: "catalogAncestors",
      catalogOrphans: "catalogOrphans",
    }),
    loading() {
      return false;
    },
    pageTitle() {
      return this.$t("menu.insure");
    },
    roots() {
      return this.catalogRoots || [];
    },
    orphans() {
      return this.catalogOrphans || [];
    },

    // ── selection ──
    selectedNode() {
      if (this.selectedId == null || !this.catalogById) return null;
      return this.catalogById.get(this.selectedId) || null;
    },
    selectionPathIds() {
      // root → selected, inclusive of the selected id itself.
      if (this.selectedId == null) return [];
      return this.ancestorsOf(this.selectedId)
        .map((n) => n.ID)
        .concat(this.selectedId);
    },
    inspectorAncestors() {
      if (this.selectedId == null) return [];
      return this.ancestorsOf(this.selectedId);
    },
    selectedChildCount() {
      return this.childCountOf(this.selectedId);
    },

    // ── drill (narrow) state ──
    drillChildren() {
      return this.childrenOf(this.drillParentId == null ? 0 : this.drillParentId);
    },
    drillBreadcrumb() {
      if (this.drillParentId == null) return [];
      const node = this.catalogById ? this.catalogById.get(this.drillParentId) : null;
      const trail = this.ancestorsOf(this.drillParentId);
      return node ? trail.concat(node) : trail;
    },

    // ── global search ──
    // Filter the FULL catalog (everything loaded — respects includeInactive
    // automatically since catalogRows reflects it) by matching the query
    // against the node's Name AND its ancestor names (path tokens). Each hit
    // becomes { node, pathNodes, isLeaf }. ~50 rows of work — a plain computed
    // is cheap enough; no debounce needed.
    searchResults() {
      const q = this.searchQuery.trim().toLowerCase();
      if (!q || !this.catalogById) return [];

      const CAP = 50;
      const scored = [];
      for (const node of this.catalogById.values()) {
        const name = String(node.Name || "");
        const lname = name.toLowerCase();
        const path = this.ancestorsOf(node.ID); // root→parent
        const matchesName = lname.indexOf(q) !== -1;
        const matchesPath =
          !matchesName &&
          path.some((p) => String(p.Name || "").toLowerCase().indexOf(q) !== -1);
        if (!matchesName && !matchesPath) continue;

        // Rank: exact name (0) > prefix (1) > name substring (2) > path-only (3).
        // Leaves edge out intermediates within the same rank (nice-to-have).
        let rank;
        if (lname === q) rank = 0;
        else if (lname.indexOf(q) === 0) rank = 1;
        else if (matchesName) rank = 2;
        else rank = 3;

        const isLeaf = this.childCountOf(node.ID) === 0;
        scored.push({ node, pathNodes: path, isLeaf, rank });
      }

      scored.sort((a, b) => {
        if (a.rank !== b.rank) return a.rank - b.rank;
        if (a.isLeaf !== b.isLeaf) return a.isLeaf ? -1 : 1; // leaves first
        return String(a.node.Name || "").localeCompare(String(b.node.Name || ""), "th");
      });

      return scored.slice(0, CAP).map(({ node, pathNodes, isLeaf }) => ({
        node,
        pathNodes,
        isLeaf,
      }));
    },
  },
  methods: {
    ...mapActions("Insure", [
      "loadCatalog",
      "createNode",
      "updateNode",
      "deactivateNode",
      "reactivateNode",
    ]),

    // ── store helpers ──
    childrenOf(id) {
      if (!this.catalogChildrenOf) return [];
      return this.catalogChildrenOf.get(id) || [];
    },
    childCountOf(id) {
      if (id == null) return 0;
      return (this.catalogChildrenOf && this.catalogChildrenOf.get(id) || []).length;
    },
    hasChildren(id) {
      return this.childrenOf(id).length > 0;
    },
    ancestorsOf(id) {
      if (id == null || typeof this.catalogAncestors !== "function") return [];
      return this.catalogAncestors(id) || [];
    },

    // ── browse: select / drill / up ──
    onSelect(node) {
      if (!node) return;
      this.selectedId = node.ID;
      // Keep drill state in sync so rotating wide→narrow lands on the node's
      // own level (in narrow mode the drill handlers own drillParentId).
      if (this.isWide) {
        this.drillParentId = node.UpperID === 0 ? null : node.UpperID;
      }
    },
    onDrill(node) {
      if (!node) return;
      this.drillParentId = node.ID;
      this.selectedId = node.ID;
    },
    onUp(node) {
      this.drillParentId = node ? node.ID : null;
    },

    // ── search: jump-to-context ──
    // Land the user in the BROWSE with the picked node selected. Wide mode:
    // selectionPathIds (already a computed walking ancestors) makes the Miller
    // columns auto-render the path. Narrow mode: open the drill at the node's
    // OWN level by pointing drillParentId at its parent (null for a root).
    // Orphans / partial ancestor chains are safe — selectionPathIds may be
    // partial, the inspector still shows the node via selectedId.
    // We CLEAR searchQuery so the result swaps back to the browse and the user
    // sees where the node lives in the tree (the whole point of jump-to-context).
    onSearchPick(node) {
      if (!node) return;
      this.selectedId = node.ID;
      this.drillParentId = node.UpperID === 0 ? null : node.UpperID;
      this.searchQuery = "";
    },

    // ── form open ──
    openCreateChild(node) {
      if (!node) return;
      this.formMode = "create-child";
      this.formNode = node;
      // ancestors for the landing preview = trail to the parent + the parent itself.
      this.formAncestors = this.ancestorsOf(node.ID).concat(node);
      this.formError = "";
      this.formShow = true;
    },
    openCreateSibling(node) {
      if (!node) return;
      this.formMode = "create-sibling";
      this.formNode = node;
      // siblings land under the same parent → preview the trail to the parent.
      this.formAncestors = this.ancestorsOf(node.ID);
      this.formError = "";
      this.formShow = true;
    },
    openEdit(node) {
      if (!node) return;
      this.formMode = "edit";
      this.formNode = node;
      this.formAncestors = this.ancestorsOf(node.ID);
      this.formError = "";
      this.formShow = true;
    },
    closeForm() {
      this.formShow = false;
      this.formError = "";
    },

    // ── form submit ──
    async onSubmitForm(payload) {
      this.formError = "";
      try {
        if (payload.mode === "create") {
          await this.createNode({
            name: payload.name,
            detail: payload.detail,
            level: payload.level,
            upperID: payload.upperID,
            dataType: payload.dataType,
          });
        } else {
          await this.updateNode({
            id: payload.id,
            name: payload.name,
            detail: payload.detail,
            dataType: payload.dataType,
            isActive: payload.isActive,
          });
        }
        // Success: store refetches/patches; keep the just-touched node selected.
        if (payload.mode === "edit") this.selectedId = payload.id;
        this.closeForm();
        this.toast("success", payload.mode === "edit" ? "บันทึกแล้ว" : "เพิ่มรายการแล้ว");
      } catch (err) {
        // Keep the form open; surface the backend reason inline.
        this.formError = this.mapError(err) || "เกิดข้อผิดพลาด กรุณาลองใหม่";
      }
    },

    // ── inspector: deactivate / reactivate / move ──
    async onDeactivate(node) {
      if (!node) return;
      try {
        await this.deactivateNode({ id: node.ID });
        this.toast("success", "ปิดใช้งานแล้ว");
        // When inactive items are hidden, the just-deactivated node should drop
        // out of the active view — refetch active-only (mirrors createNode).
        if (!this.includeInactive) {
          if (this.selectedId === node.ID) this.selectedId = null;
          await this.reload();
        }
      } catch (err) {
        // childCount is pre-disabled in the inspector, but commission-ref is
        // server-only → surface insurance_is_upper / insurance_is_using here.
        this.toast("error", this.mapError(err) || "ปิดใช้งานไม่ได้");
      }
    },
    async onReactivate(node) {
      if (!node) return;
      try {
        await this.reactivateNode({
          id: node.ID,
          name: node.Name,
          detail: node.Detail,
          dataType: node.DataType,
        });
        this.toast("success", "เปิดใช้งานแล้ว");
      } catch (err) {
        this.toast("error", this.mapError(err) || "เปิดใช้งานไม่ได้");
      }
    },
    onMove() {
      // Phase 2.
      this.toast("info", "ฟีเจอร์ย้ายรายการ — เร็วๆ นี้");
    },

    // ── error mapping ──
    mapError(err) {
      const msg =
        err && err.response && err.response.data && err.response.data.Msg;
      if (msg && ERROR_MSG[msg]) return ERROR_MSG[msg];
      // allow i18n override if keys exist; else fall back to raw / generic.
      if (msg) {
        const key = "lmg." + msg;
        const t = this.$t(key);
        return t === key ? msg : t;
      }
      return "";
    },
    toast(type, text) {
      swal.fire({
        toast: true,
        position: "top-end",
        icon: type,
        title: text,
        showConfirmButton: false,
        timer: 2200,
        timerProgressBar: true,
      });
    },

    // ── lifecycle / responsive ──
    onIncludeInactiveChange() {
      this.reload();
    },
    // Persist the DISPLAY-ONLY strip toggle so it is remembered across visits.
    // No reload needed — it only affects rendering, never the loaded data.
    onStripPrefixChange(val) {
      try {
        if (typeof window !== "undefined" && window.localStorage) {
          window.localStorage.setItem(STRIP_PREFIX_KEY, val ? "1" : "0");
        }
      } catch (e) {
        // localStorage can throw (private mode / quota) — ignore; the toggle
        // still works in-memory for this session.
      }
    },
    // Persist the DISPLAY-ONLY hide-dataType-chip toggle. No reload — render only.
    onHideTypeChipChange(val) {
      try {
        if (typeof window !== "undefined" && window.localStorage) {
          window.localStorage.setItem(HIDE_TYPE_CHIP_KEY, val ? "1" : "0");
        }
      } catch (e) {
        // localStorage can throw (private mode / quota) — ignore; the toggle
        // still works in-memory for this session.
      }
    },
    reload() {
      this.loadCatalog({ includeInactive: this.includeInactive });
    },
    applyMql(e) {
      this.isWide = e.matches;
    },
  },
  mounted() {
    this.reload();

    // Read the persisted DISPLAY-ONLY strip toggle. Safe fallback to true (the
    // default) when there's no stored value or localStorage is unavailable.
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        const stored = window.localStorage.getItem(STRIP_PREFIX_KEY);
        if (stored !== null) this.stripPrefix = stored !== "0";
      }
    } catch (e) {
      // ignore — keep the default (true).
    }

    // Read the persisted DISPLAY-ONLY hide-dataType-chip toggle. Safe fallback
    // to true (the default) when there's no stored value or localStorage fails.
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        const stored = window.localStorage.getItem(HIDE_TYPE_CHIP_KEY);
        if (stored !== null) this.hideTypeChip = stored !== "0";
      }
    } catch (e) {
      // ignore — keep the default (true).
    }

    if (typeof window !== "undefined" && window.matchMedia) {
      this._mql = window.matchMedia(WIDE_MQ);
      this.isWide = this._mql.matches;
      this._mqlHandler = (e) => this.applyMql(e);
      if (this._mql.addEventListener) {
        this._mql.addEventListener("change", this._mqlHandler);
      } else if (this._mql.addListener) {
        this._mql.addListener(this._mqlHandler);
      }
    }
  },
  beforeDestroy() {
    if (this._mql && this._mqlHandler) {
      if (this._mql.removeEventListener) {
        this._mql.removeEventListener("change", this._mqlHandler);
      } else if (this._mql.removeListener) {
        this._mql.removeListener(this._mqlHandler);
      }
    }
    this._mql = null;
    this._mqlHandler = null;
  },
};
</script>

<style scoped>
.ins-cat__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.ins-cat__search {
  flex: 1 1 240px;
  max-width: 360px;
}
.ins-cat__inactive-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  font-size: 14px;
  color: var(--c-text, #2c3e50);
  cursor: pointer;
  margin: 0;
}
.ins-cat__orphan-flag {
  color: #c0392b;
  font-weight: 600;
}

/* Layout: side-by-side when wide, stacked when narrow. */
.ins-cat__layout {
  display: grid;
  gap: 16px;
  align-items: start;
}
.ins-cat__layout.is-wide {
  grid-template-columns: minmax(0, 1fr) 360px;
}
.ins-cat__layout.is-narrow {
  grid-template-columns: 1fr;
}

.ins-cat__browse,
.ins-cat__inspector {
  padding: 16px;
  background: var(--c-surface, #fff);
  border: 1px solid var(--c-border, #e4e9f1);
  border-radius: 10px;
}
.ins-cat__inspector {
  position: sticky;
  top: 16px;
}
.ins-cat__layout.is-narrow .ins-cat__inspector {
  position: static;
}

.ins-cat__rows {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ins-cat__orphans {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px dashed #e0b4b4;
}
.ins-cat__orphans__head {
  color: #c0392b;
  font-weight: 600;
  font-size: 13px;
  margin-bottom: 8px;
}

.ins-cat__state {
  padding: 24px 8px;
  color: #8a96a3;
  font-size: 14px;
  text-align: center;
}
</style>
